import type { Row } from '../../../core/view-manager/row.js';
import type {
  BarState,
  PositionTransform,
  PositionTransformCtx,
} from '../extensions/types.js';
import type { GanttSingleView } from '../gantt-view-manager.js';

/**
 * Threads a bar state through every registered
 * {@link PositionTransform} in priority order. The first transform sees
 * the raw cell values; each subsequent transform sees the previous
 * result. The return value is the final committed state.
 */
export function runPositionPipeline(
  initial: BarState,
  transforms: readonly PositionTransform[],
  ctx: PositionTransformCtx
): BarState {
  let state = initial;
  for (const transform of transforms) {
    state = transform.transform(state, ctx);
  }
  return state;
}

/**
 * Reads the configured start/end cells for a row and constructs the raw
 * `BarState`. Returns `null` if either column is missing a value — the
 * row is not drawn in that case.
 */
export function readBarStateFromCells(
  view: GanttSingleView,
  row: Row
): BarState | null {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return null;

  const startCell = view.cellGetOrCreate(row.rowId, startCol);
  const endCell = view.cellGetOrCreate(row.rowId, endCol);

  const startRaw = startCell.jsonValue$.value;
  const endRaw = endCell.jsonValue$.value;

  if (typeof startRaw !== 'number' || typeof endRaw !== 'number') {
    return null;
  }

  return {
    rowId: row.rowId,
    startMs: startRaw,
    endMs: endRaw,
  };
}

/**
 * Clears start/end date cells for the given rows, unscheduling them
 * without deleting the backing data-view row. Used by the bar context
 * menu's "Delete bar" action and by Backspace on a bar selection —
 * both mean "take the bar off the timeline", not "drop the record".
 */
export function clearBarStateCells(
  view: GanttSingleView,
  rowIds: readonly string[]
): void {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return;
  const startProp = view.propertyGetOrCreate(startCol);
  const endProp = view.propertyGetOrCreate(endCol);
  for (const rowId of rowIds) {
    startProp.valueSet(rowId, undefined);
    endProp.valueSet(rowId, undefined);
  }
}
